import { Section, FinalCTA } from "@/components/v2/blocks";
import HowSteps from "@/components/v2/HowSteps";
import { CardContent } from "@/components/ui/card";
import TraceCard from "@/components/v2/TraceCard";
import { Check, ArrowRight, Scale, Repeat, Truck, ReceiptText } from "lucide-react";
import { Link } from "react-router-dom";
import Seo from "@/components/v2/Seo";
import PageHeroPhoto from "@/components/v2/PageHeroPhoto";

export default function Particuliers() {
  return (
    <div>
      <Seo title="Énergie pour les particuliers" description="Comparez les offres, changez de fournisseur, gérez un déménagement ou comprenez votre facture : Energy Market accompagne les particuliers." path="/particuliers" />
      <PageHeroPhoto
        cmsKey="part"
        eyebrow="Univers Particuliers"
        title="Un accompagnement simple pour mieux comprendre votre énergie"
        subtitle="Choisir une offre, comprendre une facture ou préparer un déménagement n'est pas toujours évident. Energy Market vous accompagne dans l'analyse de votre situation afin de mieux comprendre vos contrats d'électricité et de gaz."
        image="/img/nouveau-hero-particuliers.jpg"
        overlay="brand-light"
        parallax="fixed"
        highlightImages={[
          "/img/pictogrammes_energy_market_svg/comparaison_offres.svg",
          "/img/pictogrammes_energy_market_svg/changement_fournisseur.svg",
          "/img/pictogrammes_energy_market_svg/sans_engagement.svg",
          "/img/pictogrammes_energy_market_svg/pro_suivi.svg",
        ]}
        highlights={[
          "Comparaison des offres du marché",
          "Changement de fournisseur simplifié",
          "Étude gratuite et sans engagement",
          "Un interlocuteur du début à la fin",
        ]}
      />

      {/* NOS SERVICES — contenu des 4 pages Particuliers */}
      <Section cmsKey="part.aide" eyebrow="Comment pouvons-nous vous aider ?" title="Quatre accompagnements pour les particuliers">
        <div className="grid md:grid-cols-2 gap-6">

          {/* Comparer les offres */}
          <TraceCard index={0} radius={24} accent="#2E7D62" className="rounded-3xl border border-border bg-card text-card-foreground flex flex-col h-full">
            <CardContent className="p-7 flex flex-col flex-1">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl text-white shadow-sm transition-transform duration-300 group-hover:scale-110" style={{ background: "#2E7D62" }}>
                <Scale className="h-6 w-6" strokeWidth={2.1} aria-hidden />
              </div>
              <p className="em-eyebrow mb-2">Comparer les offres</p>
              <h3 className="font-display text-xl font-bold text-primary leading-snug">
                Comparez les offres d'énergie en toute simplicité
              </h3>
              <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
                Face à la diversité des fournisseurs, il n'est pas toujours facile d'identifier la solution adaptée à son foyer. Energy Market vous accompagne dans l'analyse de votre situation.
              </p>
              <p className="text-sm font-semibold text-primary mt-5 mb-2">Faire le point sur sa situation :</p>
              <ul className="space-y-1.5 text-sm text-muted-foreground">
                {["Mieux comprendre les offres disponibles", "Faire le point sur sa situation", "Identifier les solutions adaptées à ses besoins", "Obtenir un regard extérieur sur son contrat"].map((x) => (
                  <li key={x} className="flex gap-2"><Check className="h-4 w-4 text-primary mt-0.5 shrink-0" />{x}</li>
                ))}
              </ul>
              <p className="text-sm font-semibold text-primary mt-5 mb-2">Au-delà du prix :</p>
              <ul className="space-y-1.5 text-sm text-muted-foreground">
                {["Les caractéristiques du contrat", "Les services associés", "Les modalités de gestion", "Les besoins spécifiques du foyer"].map((x) => (
                  <li key={x} className="flex gap-2"><Check className="h-4 w-4 text-primary mt-0.5 shrink-0" />{x}</li>
                ))}
              </ul>
              <div className="mt-auto pt-6">
                <Link to="/particuliers/comparer-les-offres" className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline">
                  En savoir plus <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </CardContent>
          </TraceCard>

          {/* Changer de fournisseur */}
          <TraceCard index={1} radius={24} accent="#7AB317" className="rounded-3xl border border-border bg-card text-card-foreground flex flex-col h-full">
            <CardContent className="p-7 flex flex-col flex-1">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl text-white shadow-sm transition-transform duration-300 group-hover:scale-110" style={{ background: "#7AB317" }}>
                <Repeat className="h-6 w-6" strokeWidth={2.1} aria-hidden />
              </div>
              <p className="em-eyebrow mb-2">Changer de fournisseur</p>
              <h3 className="font-display text-xl font-bold text-primary leading-snug">
                Changer de fournisseur en toute sérénité
              </h3>
              <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
                De nombreux consommateurs pensent que changer de fournisseur est compliqué. En réalité, il n'y a généralement aucune interruption d'alimentation.
              </p>
              <p className="text-sm font-semibold text-primary mt-5 mb-2">Les idées reçues :</p>
              <ul className="space-y-1.5 text-sm text-muted-foreground">
                {[
                  "Vais-je subir une coupure ? Non.",
                  "Dois-je changer de compteur ? Non.",
                  "Dois-je effectuer des travaux ? Non.",
                  "Les démarches sont-elles compliquées ? Non.",
                ].map((x) => (
                  <li key={x} className="flex gap-2"><Check className="h-4 w-4 text-primary mt-0.5 shrink-0" />{x}</li>
                ))}
              </ul>
              <p className="text-sm font-semibold text-primary mt-5 mb-2">Notre accompagnement :</p>
              <ul className="space-y-1.5 text-sm text-muted-foreground">
                {["Analyse de la situation", "Étude des solutions disponibles", "Accompagnement dans la compréhension des démarches", "Réponse à vos questions"].map((x, i) => (
                  <li key={x} className="flex gap-2">
                    <span className="font-bold text-accent text-xs w-4 shrink-0 mt-0.5">{String(i+1).padStart(2,'0')}</span>{x}
                  </li>
                ))}
              </ul>
              <div className="mt-auto pt-6">
                <Link to="/particuliers/changer-de-fournisseur" className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline">
                  En savoir plus <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </CardContent>
          </TraceCard>

          {/* Déménagement */}
          <TraceCard index={2} radius={24} accent="#0E9C8A" className="rounded-3xl border border-border bg-card text-card-foreground flex flex-col h-full">
            <CardContent className="p-7 flex flex-col flex-1">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl text-white shadow-sm transition-transform duration-300 group-hover:scale-110" style={{ background: "#0E9C8A" }}>
                <Truck className="h-6 w-6" strokeWidth={2.1} aria-hidden />
              </div>
              <p className="em-eyebrow mb-2">Déménagement</p>
              <h3 className="font-display text-xl font-bold text-primary leading-snug">
                Préparez sereinement votre déménagement
              </h3>
              <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
                Un déménagement implique de nombreuses démarches. Les contrats d'électricité et de gaz sont à anticiper pour éviter les mauvaises surprises lors de l'installation.
              </p>
              <p className="text-sm font-semibold text-primary mt-5 mb-2">Avant, pendant, après :</p>
              <ul className="space-y-1.5 text-sm text-muted-foreground">
                {[
                  "Avant le départ — préparation des démarches",
                  "Avant l'arrivée — organisation des contrats",
                  "Après l'installation — vérification des informations",
                ].map((x, i) => (
                  <li key={x} className="flex gap-2">
                    <span className="font-bold text-accent text-xs w-4 shrink-0 mt-0.5">{String(i+1).padStart(2,'0')}</span>{x}
                  </li>
                ))}
              </ul>
              <p className="text-sm font-semibold text-primary mt-5 mb-2">L'occasion de faire le point :</p>
              <ul className="space-y-1.5 text-sm text-muted-foreground">
                {["Réévaluer ses besoins", "Mieux comprendre son contrat", "Étudier les solutions disponibles"].map((x) => (
                  <li key={x} className="flex gap-2"><Check className="h-4 w-4 text-primary mt-0.5 shrink-0" />{x}</li>
                ))}
              </ul>
              <div className="mt-auto pt-6">
                <Link to="/particuliers/demenagement" className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline">
                  En savoir plus <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </CardContent>
          </TraceCard>

          {/* Comprendre sa facture */}
          <TraceCard index={3} radius={24} accent="#2F7DB8" className="rounded-3xl border border-border bg-card text-card-foreground flex flex-col h-full">
            <CardContent className="p-7 flex flex-col flex-1">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl text-white shadow-sm transition-transform duration-300 group-hover:scale-110" style={{ background: "#2F7DB8" }}>
                <ReceiptText className="h-6 w-6" strokeWidth={2.1} aria-hidden />
              </div>
              <p className="em-eyebrow mb-2">Comprendre sa facture</p>
              <h3 className="font-display text-xl font-bold text-primary leading-snug">
                Comprendre sa facture d'énergie simplement
              </h3>
              <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
                Une facture contient de nombreuses informations utiles. Energy Market vous aide à les interpréter pour mieux comprendre votre situation énergétique.
              </p>
              <p className="text-sm font-semibold text-primary mt-5 mb-2">Ce que contient votre facture :</p>
              <ul className="space-y-1.5 text-sm text-muted-foreground">
                {["Coordonnées du contrat", "Abonnement", "Consommation", "Taxes et contributions", "Montant total"].map((x) => (
                  <li key={x} className="flex gap-2"><Check className="h-4 w-4 text-primary mt-0.5 shrink-0" />{x}</li>
                ))}
              </ul>
              <p className="text-sm font-semibold text-primary mt-5 mb-2">PDL, PRM, PCE — de quoi parle-t-on ?</p>
              <ul className="space-y-1.5 text-sm text-muted-foreground">
                {[
                  "PDL / PRM — Point de Livraison (électricité)",
                  "PCE — Point de Comptage et d'Estimation (gaz)",
                ].map((x) => (
                  <li key={x} className="flex gap-2"><Check className="h-4 w-4 text-primary mt-0.5 shrink-0" />{x}</li>
                ))}
              </ul>
              <div className="mt-auto pt-6">
                <Link to="/particuliers/comprendre-sa-facture" className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline">
                  En savoir plus <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </CardContent>
          </TraceCard>

        </div>
      </Section>

      <Section cmsKey="part.how" eyebrow="Comment ça fonctionne ?" title="Quatre étapes simples" muted>
        <HowSteps steps={[
          { title: "Vous nous contactez", text: "Ou vous transmettez votre facture." },
          { title: "Nous analysons", text: "Nous étudions votre situation." },
          { title: "Nous comparons", text: "Nous étudions les solutions adaptées à vos besoins." },
          { title: "Nous vous accompagnons", text: "Nous vous guidons dans votre réflexion." },
        ]} />
      </Section>

      <FinalCTA title="Mieux comprendre votre énergie, gratuitement" />
    </div>
  );
}
